import * as Sentry from "@sentry/react";
import type { TFunction } from "i18next";
import type { GetReadyTaskInstance } from "../../types/task";
import {
	GET_READY_TASK_CATALOG,
	UNKNOWN_GET_READY_TASK_FALLBACK,
} from "./catalog";
import {
	type GetReadyRequiredDocument,
	type GetReadyTaskCategory,
	type GetReadyTaskProgressMeta,
	type GetReadyTaskRenderModel,
	isGetReadyTaskCategory,
	isGetReadyTaskKey,
} from "./types";

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

const CUSTOM_TASK_TYPE_ICONS: Record<string, string> = {
	questionnaire: "fill",
	upload_doc: "upload",
	notify: "notify",
	test: "test",
	monitor: "monitor",
	book: "book",
	sign: "review-sign",
};

function resolveCustomTaskIcon(
	meta: Record<string, unknown> | undefined,
): string {
	if (!isPlainObject(meta)) return "book";
	const taskType = meta.taskType;
	if (typeof taskType === "string" && taskType in CUSTOM_TASK_TYPE_ICONS) {
		return CUSTOM_TASK_TYPE_ICONS[taskType];
	}
	return "book";
}

function extractDeadlineText(
	meta: Record<string, unknown>,
	taskKey: string,
): string | undefined {
	// Prefer deadlineLabel (new backend contract) over legacy deadlineText
	for (const field of ["deadlineLabel", "deadlineText"] as const) {
		if (field in meta) {
			const raw = meta[field];
			if (typeof raw === "string") return raw;
			Sentry.addBreadcrumb({
				category: "get-ready",
				message: `Malformed GetReady task meta.${field} (non-string)`,
				level: "warning",
				data: { taskKey },
			});
		}
	}
	return undefined;
}

function extractCategory(
	meta: Record<string, unknown>,
	taskKey: string,
): GetReadyTaskCategory | undefined {
	if (!("category" in meta)) return undefined;
	const raw = meta.category;
	if (isGetReadyTaskCategory(raw)) return raw;
	Sentry.addBreadcrumb({
		category: "get-ready",
		message: "Malformed GetReady task meta.category (unknown value)",
		level: "warning",
		data: { taskKey, category: raw },
	});
	return undefined;
}

function extractProgress(
	instance: {
		progress?: {
			current?: number;
			total?: number;
			unit?: string;
			percent?: number;
		};
		meta?: Record<string, unknown>;
	},
	taskKey: string,
): GetReadyTaskProgressMeta | undefined {
	// Prefer top-level schema progress field over legacy meta.progress
	if (instance.progress) {
		const p = instance.progress;
		if (
			typeof p.current === "number" &&
			typeof p.total === "number" &&
			typeof p.unit === "string"
		) {
			const percent =
				typeof p.percent === "number"
					? p.percent
					: p.total > 0
						? Math.round((p.current / p.total) * 100)
						: 0;
			return { current: p.current, total: p.total, unit: p.unit, percent };
		}
	}
	// Legacy fallback: meta.progress with pre-computed percent
	const meta = instance.meta;
	if (!meta || !("progress" in meta)) return undefined;
	const raw = meta.progress;
	if (
		isPlainObject(raw) &&
		typeof raw.current === "number" &&
		typeof raw.total === "number" &&
		typeof raw.unit === "string"
	) {
		const percent =
			typeof raw.percent === "number"
				? raw.percent
				: raw.total > 0
					? Math.round((raw.current / raw.total) * 100)
					: 0;
		return { current: raw.current, total: raw.total, unit: raw.unit, percent };
	}
	Sentry.addBreadcrumb({
		category: "get-ready",
		message: "Malformed GetReady task meta.progress (unexpected shape)",
		level: "warning",
		data: { taskKey },
	});
	return undefined;
}

function extractRequiredDocuments(
	meta: Record<string, unknown>,
	taskKey: string,
): GetReadyRequiredDocument[] | undefined {
	if (!("requiredDocuments" in meta)) return undefined;
	const raw = meta.requiredDocuments;
	if (!Array.isArray(raw)) {
		Sentry.addBreadcrumb({
			category: "get-ready",
			message: "Malformed GetReady task meta.requiredDocuments (non-array)",
			level: "warning",
			data: { taskKey },
		});
		return undefined;
	}
	const VALID_STATUSES = new Set(["pending", "uploaded", "completed"]);
	const docs: GetReadyRequiredDocument[] = [];
	for (const item of raw) {
		if (
			isPlainObject(item) &&
			typeof item.key === "string" &&
			typeof item.status === "string" &&
			VALID_STATUSES.has(item.status)
		) {
			docs.push({
				key: item.key,
				label: typeof item.label === "string" ? item.label : undefined,
				status: item.status as GetReadyRequiredDocument["status"],
			});
		}
	}
	return docs.length > 0 ? docs : undefined;
}

export function mapGetReadyTaskInstanceToRenderModel(
	instance: GetReadyTaskInstance,
	t: TFunction,
	locale?: string,
): GetReadyTaskRenderModel {
	const taskKey = instance.taskKey;

	let title: string;
	let subtext: string | undefined;
	let iconName: string;

	if (instance.kind === "custom") {
		title = instance.title;
		subtext = instance.description ?? undefined;
		iconName = resolveCustomTaskIcon(instance.meta);
	} else if (isGetReadyTaskKey(taskKey)) {
		const entry = GET_READY_TASK_CATALOG[taskKey];
		title = t(entry.titleKey);
		subtext = t(entry.subtextKey);
		iconName = entry.iconName;
	} else {
		title = t(UNKNOWN_GET_READY_TASK_FALLBACK.titleKey);
		subtext = t(UNKNOWN_GET_READY_TASK_FALLBACK.subtextKey);
		iconName = UNKNOWN_GET_READY_TASK_FALLBACK.iconName;
		Sentry.captureMessage("Unknown GetReady taskKey from /api/tasks", {
			level: "warning",
			extra: {
				taskKey,
				status: instance.status,
				order: instance.order,
			},
		});
	}

	let deadlineText: string | undefined;
	let category: GetReadyTaskCategory | undefined;
	let requiredDocuments: GetReadyRequiredDocument[] | undefined;

	const progress = extractProgress(instance, taskKey);

	if (instance.meta !== undefined) {
		if (!isPlainObject(instance.meta)) {
			Sentry.addBreadcrumb({
				category: "get-ready",
				message: "Malformed GetReady task meta (non-object)",
				level: "warning",
				data: { taskKey },
			});
		} else {
			deadlineText = extractDeadlineText(instance.meta, taskKey);
			category = extractCategory(instance.meta, taskKey);
			requiredDocuments = extractRequiredDocuments(instance.meta, taskKey);
		}
	}

	if (instance.kind === "custom" && !deadlineText && instance.deadline) {
		const d = new Date(instance.deadline);
		if (!Number.isNaN(d.getTime())) {
			const formatted = new Intl.DateTimeFormat(locale, {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				timeZone: "UTC",
			}).format(d);
			deadlineText = t("customTask.dueBy", { date: formatted });
		}
	}

	return {
		id: instance.id,
		kind: instance.kind,
		taskKey,
		status: instance.status,
		order: instance.order,
		deadlineText,
		title,
		subtext,
		iconName,
		category,
		progress,
		requiredDocuments,
	};
}
